import React, { useState, useRef } from 'react'
import { useTranslation } from '../../../shared/hooks/useIntractionTranslate'
import { Box, IconButton, Typography } from '@mui/material'
import { ArrowsPointingOutIcon, ChevronLeftIcon } from '@heroicons/react/24/outline'
import { INTERAX_COLORS, INTERAX_TYPOGRAPHY } from '../../../constants/colors'
import { Button as DesignSystemButton } from '../../../interaxDesignSystemComponents'
import { DefaultModal } from '../../../shared/components/DefaultModal'

interface VideoModalV2Props {
  isOpen: boolean
  onClose: () => void
  onComplete: () => void
  videoUrl: string
  description: string
  className?: string
}

/**
 * VideoModalV2 - Video modal using DefaultModal as base
 * Shows video player with controls and completion tracking
 */
export const VideoModalV2: React.FC<VideoModalV2Props> = ({
  isOpen,
  onClose,
  onComplete,
  videoUrl,
  description,
  className = ''
}) => {
  const { t } = useTranslation()
  const videoRef = useRef<HTMLVideoElement>(null)
  const [videoEnded, setVideoEnded] = useState(false)

  const handleVideoEnd = () => {
    setVideoEnded(true)
    onComplete()
  }

  const handleMarkComplete = () => {
    onComplete()
    onClose()
  }

  const handleFullscreen = () => {
    if (videoRef.current) {
      if (videoRef.current.requestFullscreen) {
        videoRef.current.requestFullscreen()
      }
    }
  }

  return (
    <DefaultModal
      isOpen={isOpen}
      onClose={onClose}
      className={className}
      showCloseButton={false}
      maxWidth="800px"
      backgroundColor={INTERAX_COLORS.offBlack}
    >
      {/* Back to Onboarding Button - Inside Modal */}
      <Box sx={{ mb: 3 }}>
        <DesignSystemButton
          variant="secondary"
          onClick={onClose}
          icon={<ChevronLeftIcon className="w-4 h-4" />}
          iconPosition="left"
        >
          {t('gamer.onboardingModal.backToOnboarding')}
        </DesignSystemButton>
      </Box>
      {/* Video Player */}
      <Box sx={{ position: 'relative', pb: '56.25%', height: 0, overflow: 'hidden', mb: 3 }}>
        <video
          ref={videoRef}
          src={videoUrl}
          controls
          onEnded={handleVideoEnd}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: INTERAX_COLORS.offBlack
          }}
        />
        <IconButton
          onClick={handleFullscreen}
          sx={{
            position: 'absolute',
            bottom: 8,
            right: 8,
            color: INTERAX_COLORS.white,
            backgroundColor: 'rgba(0,0,0,0.5)',
            '&:hover': {
              backgroundColor: 'rgba(0,0,0,0.7)'
            },
            zIndex: 10
          }}
        >
          <ArrowsPointingOutIcon className="w-6 h-6" />
        </IconButton>
      </Box>

      {/* Video Description */}
      <Typography
        sx={{
          ...INTERAX_TYPOGRAPHY.body2,
          color: INTERAX_COLORS.white,
          mb: 3
        }}
      >
        {description}
      </Typography>

      {/* Video Completion Status */}
      {videoEnded && (
        <Typography
          sx={{
            ...INTERAX_TYPOGRAPHY.body1,
            color: INTERAX_COLORS.green,
            fontWeight: 600,
            mb: 3
          }}
        >
          {t('gamer.onboardingModal.videoCompleted')} {t('gamer.onboardingModal.continueToNext')}
        </Typography>
      )}

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
        {!videoEnded && (
          <DesignSystemButton variant="primary" onClick={handleMarkComplete}>
            {t('gamer.onboardingModal.markComplete')}
          </DesignSystemButton>
        )}

        {videoEnded && (
          <DesignSystemButton variant="primary" onClick={onClose}>
            {t('gamer.onboardingModal.continueToNext')}
          </DesignSystemButton>
        )}
      </Box>
    </DefaultModal>
  )
}
